import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import moment from 'moment'
import { Column, Row } from 'react-table'
import { Box, Typography, Avatar } from '@mui/material'

interface Customer {
  id: string
  name: string
  responsible_user?: string
  local_id?: string
  created_at?: string
  accounts?: number
  last_login?: string
  users?: unknown[]
}

const getInitials = (name: string): string => {
  if (!name) return '?'
  const words = name.split(' ').filter(Boolean)
  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase()
  }
  return (words[0][0] + words[words.length - 1][0]).toUpperCase()
}

function useColumns(): Column<Customer>[] {
  const intl = useIntl()

  const columns: Column<Customer>[] = [
    {
      Header: 'Company',
      minWidth: 250,
      accessor: 'name',
      Cell: ({ row }: { row: Row<Customer> }) => {
        const { name, id } = row.original
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={(theme) => ({
                width: 36,
                height: 36,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(84, 189, 203, 0.15)'
                    : 'primary.50',
                color: 'primary.main',
                fontSize: 13,
                fontWeight: 700,
              })}
            >
              {getInitials(name)}
            </Avatar>
            <Box>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: 14,
                  color: 'text.primary',
                  lineHeight: 1.3,
                }}
              >
                {name}
              </Typography>
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'text.disabled',
                }}
              >
                ID: {id}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      Header: 'Responsible',
      minWidth: 140,
      accessor: 'responsible_user',
      Cell: ({ row }: { row: Row<Customer> }) => {
        const responsibleUser = row.original.responsible_user
        if (!responsibleUser) return null
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar
              sx={(theme) => ({
                width: 26,
                height: 26,
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? 'rgba(255, 183, 90, 0.2)'
                    : 'warning.100',
                color: theme.palette.mode === 'dark' ? '#FFCC89' : 'warning.dark',
                fontSize: 10,
                fontWeight: 700,
              })}
            >
              {getInitials(responsibleUser)}
            </Avatar>
            <Typography sx={{ fontSize: 14, color: 'text.primary' }}>
              {responsibleUser}
            </Typography>
          </Box>
        )
      },
    },
    {
      Header: 'Local ID',
      minWidth: 100,
      accessor: 'local_id',
    },
    {
      Header: 'Created',
      minWidth: 100,
      accessor: 'created_at',
      Cell: ({ row }: { row: Row<Customer> }) =>
        row.original?.created_at ? intl.formatDate(row.original.created_at) : null,
    },
    {
      Header: 'Users',
      accessor: 'accounts',
      minWidth: 70,
    },
    {
      Header: 'Last Login',
      minWidth: 110,
      accessor: 'users',
      Cell: ({ row }: { row: Row<Customer> }) => {
        const lastLogin = (row.original as Customer & { last_login?: string })?.last_login
        return lastLogin ? moment(lastLogin).fromNow() : null
      },
    },
  ]

  const memoizedColumns = useMemo(() => columns, [intl])
  return memoizedColumns
}

export default useColumns
